/**
 * Pagination Utility Functions
 */

import { PaginationMeta } from './types';

/**
 * Calculate pagination range for page numbers display
 * Shows current page with context (e.g., 1 2 [3] 4 5 ... 10)
 */
export function getPaginationRange(
  currentPage: number,
  totalPages: number,
  siblingCount: number = 1
): (number | 'ellipsis')[] {
  const totalPageNumbers = siblingCount + 5; // First, last, current, and siblings

  // If total pages is less than the page numbers we want to show, return all
  if (totalPageNumbers >= totalPages) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const shouldShowLeftEllipsis = leftSiblingIndex > 2;
  const shouldShowRightEllipsis = rightSiblingIndex < totalPages - 1;

  const firstPageIndex = 1;
  const lastPageIndex = totalPages;

  // No ellipsis needed
  if (!shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis', totalPages];
  }

  // Right ellipsis only
  if (shouldShowLeftEllipsis && !shouldShowRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );
    return [firstPageIndex, 'ellipsis', ...rightRange];
  }

  // Both ellipsis
  if (shouldShowLeftEllipsis && shouldShowRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [firstPageIndex, 'ellipsis', ...middleRange, 'ellipsis', lastPageIndex];
  }

  return [];
}

/**
 * Get URL search params for pagination
 */
export function getPaginationParams(searchParams: URLSearchParams): {
  page: number;
  limit: number;
} {
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '50', 10);

  return {
    page: isNaN(page) || page < 1 ? 1 : page,
    limit: isNaN(limit) || limit < 1 ? 50 : limit,
  };
}

/**
 * Update URL search params with pagination values
 */
export function updatePaginationParams(
  searchParams: URLSearchParams,
  page: number,
  limit: number
): URLSearchParams {
  const newParams = new URLSearchParams(searchParams);
  newParams.set('page', page.toString());
  newParams.set('limit', limit.toString());
  return newParams;
}

/**
 * Calculate the start and end index for current page items
 */
export function getItemRange(
  currentPage: number,
  pageSize: number,
  totalCount: number
): { start: number; end: number } {
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalCount);
  return { start, end };
}

/**
 * Format pagination info text
 */
export function formatPaginationInfo(
  currentPage: number,
  pageSize: number,
  totalCount: number,
  itemName: string = 'items'
): string {
  if (totalCount === 0) {
    return `No ${itemName} found`;
  }

  const { start, end } = getItemRange(currentPage, pageSize, totalCount);
  return `Showing ${start}-${end} of ${totalCount} ${itemName}`;
}

/**
 * Get localStorage key for pagination persistence
 */
export function getPaginationStorageKey(key: string): string {
  return `pagination_${key}`;
}

/**
 * Load pagination state from localStorage
 */
export function loadPaginationState(storageKey: string): {
  page?: number;
  limit?: number;
} | null {
  try {
    const stored = localStorage.getItem(getPaginationStorageKey(storageKey));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load pagination state:', error);
  }
  return null;
}

/**
 * Save pagination state to localStorage
 */
export function savePaginationState(storageKey: string, page: number, limit: number): void {
  try {
    localStorage.setItem(getPaginationStorageKey(storageKey), JSON.stringify({ page, limit }));
  } catch (error) {
    console.error('Failed to save pagination state:', error);
  }
}

/**
 * Check if a page number is valid
 */
export function isValidPage(page: number, totalPages: number): boolean {
  return page >= 1 && page <= totalPages && Number.isInteger(page);
}

/**
 * Clamp a page number to valid range
 */
export function clampPage(page: number, totalPages: number): number {
  return Math.max(1, Math.min(page, totalPages || 1));
}
